import {
  PartialQueryVariables,
  TagsQueryWithFilters,
  TagsQueryWithFilters_tagsConnection_nodes,
  TagsQueryWithFiltersGql,
  TagsQueryWithFiltersVariables,
} from '@tableau/catalog-ui-temp';
import { defaultMemoize } from 'reselect';

import { ListResult } from '../../httpClient/fetchers/ListResult';
import { IFieldValue } from '../../httpClient/serverApi/types/IFieldValue';
import { SliceableHelper } from '../../httpClient/utils/SliceableHelper';
import { ILeafFilterClause } from '../../spacesPlatform/models/IFilterClause';
import { catalogApolloClient } from '../CatalogApolloClient';
import { getExplorerGraphQLFilters } from './CatalogFilterClauseHelpers';
import { GraphQLSliceableFactory, makeSliceableFactoryForGraphQLQuery } from './GraphQLSliceable';

type FieldValueResultItem = IFieldValue & { id: string };

export type FieldValueContentType = 'database' | 'table';

const getSliceableFactoryForExternalContentTags = defaultMemoize((contentType: FieldValueContentType) => {
  return makeSliceableFactoryForGraphQLQuery<
    TagsQueryWithFilters_tagsConnection_nodes,
    FieldValueResultItem,
    TagsQueryWithFiltersVariables,
    TagsQueryWithFilters
    >(
    {
      queryGql: TagsQueryWithFiltersGql,
      selectConnectionListResult: results => {
        const filteredItems = results?.tagsConnection?.nodes.filter(node =>
          contentType === 'table' ? node.databaseTablesConnection.totalCount > 0 :
          node.databasesConnection.totalCount > 0,
        );
        return {
          nodes: filteredItems,
          totalCount: filteredItems.length,
        };
      },
      mapResultNode:(tag, _) => ({
        label: tag.name,
        value: tag.name,
        count: contentType === 'table' ? tag.databaseTablesConnection.totalCount : tag.databasesConnection.totalCount,
        id: tag.id
      }),
    },
    catalogApolloClient
  );
});

export class GraphQLTagValuesQuery {

  private filterClauses: ILeafFilterClause[];
  private matchText: string;
  private hasActiveWarningFilter: boolean | null;
  private isCertifiedFilter: boolean | null;
  private sliceableFactory: GraphQLSliceableFactory<TagsQueryWithFiltersVariables, FieldValueResultItem>;

  constructor(
    private contentType: FieldValueContentType,
    ) {
      this.sliceableFactory = getSliceableFactoryForExternalContentTags(contentType);
    }

  public getSlice(start: number, size: number): Promise<ListResult<IFieldValue>> {
    const filterParameters = getExplorerGraphQLFilters(this.getFilters());

    const partialQueryVariables: PartialQueryVariables<TagsQueryWithFiltersVariables> = {
      name: this.matchText === '' ? null : this.matchText,
      textSearch: filterParameters.search,
      hasActiveWarningFilter: this.hasActiveWarningFilter,
      isCertifiedFilter: this.isCertifiedFilter,
      isEmbeddedFilter: undefined,
      connectionTypeFilter: null,
    };

    const sliceable = this.sliceableFactory(partialQueryVariables);

    // fetching all items and filtering client-side, until we implement server-side filtering via CRASS
    // in TFS 1122638
    return SliceableHelper.fetchAll(sliceable);
  }

  public getFilters(): ILeafFilterClause[] {
    return this.filterClauses;
  }

  public withMatchText(matchText: string): GraphQLTagValuesQuery {
    return this.clone().setMatchText(matchText);
  }

  public withFilters(filterClauses: ILeafFilterClause[]): GraphQLTagValuesQuery {
    return this.clone().setFilterClauses(filterClauses);
  }

  private clone(): GraphQLTagValuesQuery {
    return new GraphQLTagValuesQuery(this.contentType)
      .setFilterClauses(this.filterClauses)
      .setMatchText(this.matchText);
  }

  private setFilterClauses(filterClauses: ILeafFilterClause[]): GraphQLTagValuesQuery {
    this.filterClauses = filterClauses;

    const filterParameters = getExplorerGraphQLFilters(filterClauses ?? []);
    this.hasActiveWarningFilter = filterParameters.hasActiveWarning;
    this.isCertifiedFilter = filterParameters.isCertified;

    return this;
  }

  private setMatchText(matchText: string): GraphQLTagValuesQuery {
    this.matchText = matchText;
    return this;
  }
}
